import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { REACT_APP_BACKEND_URL, REACT_APP_SOCKET_PATH } from '../../utils/config';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
  accountId: number | null;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, accountId }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    console.log('[Socket] Initializing connection');

    const socketInstance = io(REACT_APP_BACKEND_URL!, {
      path: REACT_APP_SOCKET_PATH,
      transports: ['websocket'],
      withCredentials: true,
    });

    socketInstance.on('connect', () => {
      console.log('[Socket] Connected');
      setIsConnected(true);

      if (accountId !== null) {
        socketInstance.emit('join', accountId);
        console.log(`[Socket] Joined room: account-${accountId}`);
      }
    });

    socketInstance.on('disconnect', () => {
      console.log('[Socket] Disconnected');
      setIsConnected(false);
    });

    setSocket(socketInstance);

    return () => {
      console.log('[Socket] Cleaning up');
      socketInstance.disconnect();
    };
  }, [accountId]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): Socket | null => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }

  return context.socket;
};

export const useSocketConnection = (): SocketContextType => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error('useSocketConnection must be used within a SocketProvider');
  }

  return context;
};